import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Wallet, Landmark, Smartphone } from "lucide-react";

const TIPE = [
  { v: "kas", l: "Kas / Tunai" },
  { v: "bank", l: "Rekening Bank" },
  { v: "ewallet", l: "E-Wallet" },
  { v: "lainnya", l: "Lainnya" },
];
const tl = (v) => TIPE.find((x) => x.v === v)?.l || v;
const Icon = ({ t }) => (t === "bank" ? <Landmark size={16} /> : t === "ewallet" ? <Smartphone size={16} /> : <Wallet size={16} />);

export default function AkunPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { nama: "", tipe: "bank", saldo_awal: 0, keterangan: "" };
  const [form, setForm] = useState(empty);

  const load = async () => { const r = await api.get("/akun"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    if (!form.nama?.trim()) { toast.error("Nama akun wajib diisi"); return; }
    const payload = { ...form, saldo_awal: Number(form.saldo_awal) || 0 };
    try {
      if (editId) await api.put(`/akun/${editId}`, payload);
      else await api.post("/akun", payload);
      toast.success(editId ? "Diperbarui" : "Akun tersimpan"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delOne = async (id) => {
    if (!confirm("Hapus akun ini?")) return;
    try { await api.delete(`/akun/${id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const totalSaldo = items.reduce((s, x) => s + Number(x.saldo || 0), 0);

  return (
    <>
      <PageHeader title="Akun / Dompet" subtitle="Kelola kas, rekening bank & e-wallet. Saldo dihitung otomatis dari transaksi & transfer." actions={
        <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-akun-btn"><Plus size={14} className="mr-1" /> Tambah Akun</Button>
      } />
      <div className="p-4 md:p-8 space-y-4">
        <div className="card-flat p-5 max-w-sm"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total Saldo Semua Akun</div><div className="mt-1 font-mono text-2xl text-slate-900">{rupiah(totalSaldo)}</div></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="akun-grid">
          {items.length === 0 && <div className="text-slate-400">Belum ada akun.</div>}
          {items.map((a) => (
            <div key={a.id} className="card-flat p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-600"><span className="h-8 w-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center"><Icon t={a.tipe} /></span><span className="text-sm font-medium">{a.nama}</span></div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(a)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-akun-${a.id}`}><Pencil size={14} /></button>
                  <button onClick={() => delOne(a.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-akun-${a.id}`}><Trash2 size={14} className="text-rose-600" /></button>
                </div>
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-400">{tl(a.tipe)}</div>
              <div className="font-mono text-xl font-semibold mt-1">{rupiah(a.saldo)}</div>
              <div className="text-[11px] text-slate-400 mt-1">Saldo awal: {rupiah(a.saldo_awal)}</div>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="akun-dialog">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Akun</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Nama Akun <span className="text-rose-500">*</span></Label><Input value={form.nama} onChange={upd("nama")} placeholder="mis. BCA 1234 / GoPay" data-testid="akun-nama" /></div>
            <div className="space-y-1.5"><Label>Tipe</Label>
              <Select value={form.tipe} onValueChange={(v) => setForm((f) => ({ ...f, tipe: v }))}>
                <SelectTrigger data-testid="akun-tipe"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPE.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Saldo Awal (Rp)</Label><CurrencyInput value={form.saldo_awal} onValueChange={upd("saldo_awal")} /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button><Button onClick={save} className="brand-btn rounded-sm" data-testid="akun-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
